import React, { useState, useEffect } from 'react';
import { FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import instanciaAxios from '../../../miscelaneo/instanciaAxios';
import { toast } from 'react-toastify';
import { useContextoSesion } from '../../../miscelaneo/ContextoSesion';
import FuncionesGenerales from '../FuncionesGenerales';

const ITEMS_POR_PAGINA = 10;

export default function TablaFactura({ setFacturaSeleccionada, onClose }) {
  const [facturas, setFacturas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [cargando, setCargando] = useState(false);
  const [facturasFiltradas, setFacturasFiltradas] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const { usuario } = useContextoSesion();
  const { formatoMoneda, formatearFecha } = FuncionesGenerales(usuario);

  useEffect(() => {
    fetchFacturas();
  }, []);

  const fetchFacturas = async () => {
    try {
      setCargando(true);
      const respuesta = await instanciaAxios.get('/factura');
      if (respuesta.status === 200) {
        setFacturas(respuesta.data);
        setFacturasFiltradas(respuesta.data);
      }
    } catch (error) {
      console.error('Error al obtener facturas:', error);
      toast.error('Error al cargar las facturas');
    } finally {
      setCargando(false);
    }
  };

  // Filtrar y resetear a página 1 cuando cambia el filtro
  useEffect(() => {
    if (filtro.trim() === '') {
      setFacturasFiltradas(facturas);
    } else {
      const busqueda = filtro.toLowerCase();
      const filtered = facturas.filter(factura =>
        factura.numero_factura?.toString().toLowerCase().includes(busqueda) ||
        factura.nombre_cliente?.toLowerCase().includes(busqueda) ||
        factura.ncf?.toLowerCase().includes(busqueda)
      );
      setFacturasFiltradas(filtered);
    }
    setPaginaActual(1); // Resetear a página 1 al filtrar
  }, [filtro, facturas]);

  // Calcular paginación
  const totalPaginas = Math.ceil(facturasFiltradas.length / ITEMS_POR_PAGINA);
  const indiceInicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
  const indiceFin = Math.min(indiceInicio + ITEMS_POR_PAGINA, facturasFiltradas.length);
  const facturasPaginadas = facturasFiltradas.slice(indiceInicio, indiceFin);

  const irPaginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };

  const irPaginaSiguiente = () => {
    if (paginaActual < totalPaginas) {
      setPaginaActual(paginaActual + 1);
    }
  };

  const handleSeleccionar = (factura) => {
    if (setFacturaSeleccionada) {
      setFacturaSeleccionada(factura.id_factura);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header con título y búsqueda */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold">Seleccionar Factura</h2>
          <p className="text-sm text-gray-500">
            Haga clic en una fila para seleccionar la factura
          </p>
        </div>
        <div className="relative w-full sm:w-auto">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por #, cliente o NCF..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="pl-10 pr-4 py-2 border rounded-lg w-full sm:w-72 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            autoFocus
          />
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Tabla */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    # Factura
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    NCF
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {facturasPaginadas.map((factura) => (
                  <tr 
                    key={factura.id_factura} 
                    onClick={() => handleSeleccionar(factura)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {factura.numero_factura || factura.id_factura}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {factura.nombre_cliente || 'Sin cliente'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatearFecha(factura.fecha_hora)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                      {formatoMoneda(factura.monto_total)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                      {factura.ncf || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        factura.id_estado === 1 
                          ? 'bg-green-100 text-green-800' 
                          : factura.id_estado === 2
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {factura.id_estado === 1 ? 'Activa' : factura.id_estado === 2 ? 'Pendiente' : 'Anulada'}
                      </span>
                    </td>
                  </tr>
                ))}
                {facturasPaginadas.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <FaSearch className="text-3xl text-gray-300" />
                        <span>
                          {filtro 
                            ? 'No se encontraron facturas que coincidan con la búsqueda' 
                            : 'No hay facturas registradas'}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación y contador */}
          {facturasFiltradas.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-2">
              <span className="text-sm text-gray-600">
                Mostrando {indiceInicio + 1}-{indiceFin} de {facturasFiltradas.length} facturas
              </span>
              
              {totalPaginas > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={irPaginaAnterior}
                    disabled={paginaActual === 1}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      paginaActual === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                    }`}
                  >
                    <FaChevronLeft className="text-xs" />
                    Anterior
                  </button>
                  
                  <span className="text-sm text-gray-600 px-2">
                    Página {paginaActual} de {totalPaginas}
                  </span>
                  
                  <button
                    onClick={irPaginaSiguiente}
                    disabled={paginaActual === totalPaginas}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      paginaActual === totalPaginas
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                    }`}
                  >
                    Siguiente
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Botón cancelar (si se proporciona onClose) */}
          {onClose && (
            <div className="flex justify-end pt-2 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
